import type { MaybeRefOrGetter, Ref } from "vue";

import { computedWithControl } from "@vueuse/core";
import { isPromise } from "radash";
import { computed, inject, ref, toValue, watchEffect } from "vue";

import { injectI18nLocale } from "@solvari/translations";
import { memoize } from "@solvari/utils";

import type {
  EventsCallback,
  ValidationRule,
  ValidationRuleHydrated,
} from "@/lib/validation/validation.types";

import { required, serverSideRule } from "@/lib/validation/rules";
import { _validationObserverKey } from "@/lib/validation/ValidationObserver/useValidationObserver.ts";

type UseRulesProps<ModelValue> = Readonly<{
  name: string;
  required: boolean;
  rules: ValidationRule<NoInfer<ModelValue>>[];
  validationEvents: EventsCallback<NoInfer<ModelValue>> | string[];
}>;

function useRules<ModelValue>(
  modelValue: MaybeRefOrGetter<ModelValue>,
  props: UseRulesProps<ModelValue>,
  triggeredEvents: Ref<Set<string>>,
) {
  const validationObserver = inject(_validationObserverKey, null);
  const serverSideError = computed(() => {
    return validationObserver?.serverSideErrors.value[props.name]?.[0] ?? "";
  });

  const requiredRule = required();
  const serverSide = serverSideRule(serverSideError);
  const inferredRules = computed(() => {
    const rules = [];

    if (validationObserver) {
      rules.push(serverSide);
    }
    if (props.required) {
      rules.push(requiredRule);
    }

    return rules;
  });

  const mergedRules = computed(() => {
    return [...inferredRules.value, ...props.rules].reduce(
      (merged: ValidationRule<ModelValue>[], rule) => {
        const existingRule = merged.find(({ id }) => id === rule.id);

        if (existingRule) {
          const existingRuleIndex = merged.findIndex(
            ({ id }) => id === rule.id,
          );
          merged[existingRuleIndex] = { ...existingRule, ...rule };
        } else {
          merged.push(rule);
        }

        return merged;
      },
      [],
    );
  });

  const notRequiredAndWithoutValue = computed(() => {
    // This is necessary because most rules will fail if there's no value
    // Even if the field is not required
    return (
      !mergedRules.value.some((rule) => rule.id === required.id) &&
      !required().validate(toValue(modelValue))
    );
  });

  const locale = injectI18nLocale();

  const hydrateRule = memoize(
    (rule: ValidationRule<ModelValue>): ValidationRuleHydrated => {
      const isValidating = ref(true);
      const lastValidationPassed = ref(false);

      watchEffect(
        async () => {
          const validationPromise = rule.validate(toValue(modelValue));
          if (isPromise(validationPromise)) {
            isValidating.value = true;
            lastValidationPassed.value = await validationPromise;
            isValidating.value = false;
          } else {
            lastValidationPassed.value = validationPromise;
            isValidating.value = false;
          }
        },
        { flush: "sync" },
      );

      const isValid = computed(
        () =>
          notRequiredAndWithoutValue.value ||
          (lastValidationPassed.value && !isValidating.value),
      );

      const isInvalid = computed(() => {
        return (
          !notRequiredAndWithoutValue.value &&
          !lastValidationPassed.value &&
          !isValidating.value
        );
      });

      const events = ref<readonly string[]>([]);
      const isVisible = computed(() => {
        return (
          isInvalid.value &&
          (triggeredEvents.value.has("all") ||
            events.value.some((event) => triggeredEvents.value.has(event)))
        );
      });

      const options = rule.options(locale);

      /*
      Ref + WatchEffect simulates a computed
      It's not a computed because of a circular dependency with isVisible
      This will not result in an infinite loop because of equality checks in computed invalidation
     */
      watchEffect(() => {
        const ruleEvents = options.events || props.validationEvents;

        events.value =
          typeof ruleEvents === "function"
            ? ruleEvents(isVisible.value, toValue(modelValue))
            : ruleEvents;
      });

      return {
        id: rule.id,
        name: options.name,
        blocking: options.blocking ?? true,
        color: options.color ?? "danger",
        component: options.component,
        events,
        isValid,
        isInvalid,
        isValidating,
        isVisible,
        message: options.message,
        params: options.params,
      } as ValidationRuleHydrated;
    },
    { key: (rule) => rule },
  );

  return computedWithControl(mergedRules, () => {
    return mergedRules.value
      .map(hydrateRule)
      .toSorted(
        (first, second) => Number(second.blocking) - Number(first.blocking),
      );
  });
}

export type { UseRulesProps };
export { useRules };
